<section class="ticket-shell">
  <div class="ticket-header">
    <div class="ticket-kicker">
      <lucide-icon [img]="LifeBuoyIcon" [size]="14" />
      <span>// Support desk</span>
    </div>
    <div class="ticket-title-row">
      <div>
        <h3>{{ panelTitle() }}</h3>
        <p>{{ panelSubtitle() }}</p>
      </div>
      <span class="ticket-count">{{ tickets().length }} active</span>
    </div>
  </div>

  <div class="ticket-grid">
    @if (audience() === 'admin') {
      <form class="ticket-form" (submit)="$event.preventDefault(); sendResponse()">
        <div class="form-title">
          <div class="icon-box">
            <lucide-icon [img]="SendIcon" [size]="16" />
          </div>
          <div>
            <h4>Answer ticket</h4>
            <p>{{ selectedTicket().id }} from {{ selectedTicket().requester }}</p>
          </div>
        </div>

        @if (selectedTicket(); as ticket) {
          <div class="selected-ticket">
            <span class="ticket-id">{{ ticket.id }}</span>
            <h5>{{ ticket.subject }}</h5>
            <p>{{ ticket.message }}</p>
            <div class="ticket-footer">
              <span>{{ ticket.category }}</span>
              <span class="attachment-count">
                <lucide-icon [img]="PaperclipIcon" [size]="13" />
                {{ ticket.attachments.length }} image{{ ticket.attachments.length === 1 ? '' : 's' }}
              </span>
            </div>
          </div>
        }

        <label class="field">
          <span>Reply</span>
          <textarea
            rows="6"
            placeholder="Write response to builder"
            [value]="responseDraft()"
            (input)="updateResponseDraft($any($event.target).value)"
          ></textarea>
        </label>

        <label class="image-drop">
          <input type="file" accept="image/*" multiple (change)="onResponseImagesSelected($event)" />
          <span class="upload-icon">
            <lucide-icon [img]="UploadIcon" [size]="18" />
          </span>
          <span>
            <strong>Attach response images</strong>
            <small>Screenshots, marked-up plans, or reference photos.</small>
          </span>
        </label>

        @if (responseAttachments().length) {
          <div class="image-list">
            @for (image of responseAttachments(); track image.id) {
              <div class="image-card">
                <img [src]="image.url" [alt]="image.name" />
                <div>
                  <p>{{ image.name }}</p>
                  <span>{{ image.size }}</span>
                </div>
                <button type="button" (click)="removeResponseAttachment(image.id)" aria-label="Remove image">
                  <lucide-icon [img]="CloseIcon" [size]="14" />
                </button>
              </div>
            }
          </div>
        }

        <div class="admin-actions">
          <button type="button" class="status-button" (click)="updateTicketStatus('Resolved')">Mark resolved</button>
          <button type="button" class="status-button" (click)="updateTicketStatus('Open')">Keep open</button>
        </div>

        <button type="submit" class="submit-button" [disabled]="!canSendResponse()">
          <lucide-icon [img]="SendIcon" [size]="15" />
          <span>Send response</span>
        </button>
      </form>
    } @else {
      <form class="ticket-form" (submit)="$event.preventDefault(); createTicket()">
        <div class="form-title">
          <div class="icon-box">
            <lucide-icon [img]="PlusIcon" [size]="16" />
          </div>
          <div>
            <h4>Create ticket</h4>
            <p>Images, screenshots, and notes stay with the request.</p>
          </div>
        </div>

        <label class="field">
          <span>Subject</span>
          <input
            type="text"
            placeholder="Short ticket subject"
            [value]="subject()"
            (input)="updateSubject($any($event.target).value)"
          />
        </label>

        <div class="field-row">
          <label class="field">
            <span>Category</span>
            <select [value]="category()" (change)="updateCategory($any($event.target).value)">
              @for (item of categories(); track item) {
                <option [value]="item">{{ item }}</option>
              }
            </select>
          </label>

          <label class="field">
            <span>Priority</span>
            <select [value]="priority()" (change)="updatePriority($any($event.target).value)">
              <option value="Low">Low</option>
              <option value="Medium">Medium</option>
              <option value="High">High</option>
            </select>
          </label>
        </div>

        <label class="field">
          <span>Description</span>
          <textarea
            rows="5"
            placeholder="Describe the issue or request"
            [value]="message()"
            (input)="updateMessage($any($event.target).value)"
          ></textarea>
        </label>

        <label class="image-drop">
          <input type="file" accept="image/*" multiple (change)="onImagesSelected($event)" />
          <span class="upload-icon">
            <lucide-icon [img]="UploadIcon" [size]="18" />
          </span>
          <span>
            <strong>Attach images</strong>
            <small>PNG, JPG, or screenshots. Up to 6 images.</small>
          </span>
        </label>

        @if (attachments().length) {
          <div class="image-list">
            @for (image of attachments(); track image.id) {
              <div class="image-card">
                <img [src]="image.url" [alt]="image.name" />
                <div>
                  <p>{{ image.name }}</p>
                  <span>{{ image.size }}</span>
                </div>
                <button type="button" (click)="removeAttachment(image.id)" aria-label="Remove image">
                  <lucide-icon [img]="CloseIcon" [size]="14" />
                </button>
              </div>
            }
          </div>
        }

        <button type="submit" class="submit-button" [disabled]="!canCreateTicket()">
          <lucide-icon [img]="SendIcon" [size]="15" />
          <span>Create ticket</span>
        </button>
      </form>
    }

    <div class="ticket-list">
      <div class="list-title">
        <div class="icon-box">
          <lucide-icon [img]="TicketIcon" [size]="16" />
        </div>
        <div>
          <h4>Ticket queue</h4>
          <p>{{ audience() === 'admin' ? 'Builder conversations' : 'My conversations' }}</p>
        </div>
      </div>

      <div class="tickets">
        @for (ticket of tickets(); track ticket.id) {
          <article
            class="ticket-item"
            [ngClass]="ticket.id === selectedTicketId() ? 'ticket-item-active' : ''"
            (click)="audience() === 'admin' ? selectTicket(ticket.id) : null"
          >
            <div class="ticket-item-top">
              <span class="ticket-id">{{ ticket.id }}</span>
              <div class="badge-row">
                <span class="badge" [ngClass]="statusClass(ticket.status)">{{ ticket.status }}</span>
                <span class="badge" [ngClass]="priorityClass(ticket.priority)">{{ ticket.priority }}</span>
              </div>
            </div>
            <h5>{{ ticket.subject }}</h5>
            <p>{{ ticket.message }}</p>
            <div class="ticket-meta">
              <span>{{ ticket.requester }}</span>
              <span>{{ ticket.updatedAt }}</span>
            </div>
            <div class="ticket-footer">
              <span>{{ ticket.category }}</span>
              <span class="attachment-count">
                <lucide-icon [img]="PaperclipIcon" [size]="13" />
                {{ ticket.attachments.length }} image{{ ticket.attachments.length === 1 ? '' : 's' }}
              </span>
            </div>
          </article>
        }
      </div>
    </div>
  </div>
</section>
